'use client'

import ImageBgLoading, { ResponsiveImageBgLoading } from '@component/imageBgLoading'
import { propertyDataLayer } from '@plugin/google/gtm/modules/property'
import { global } from '@store/index'
import classNames from 'classnames'
import { concat, isFunction, map } from 'lodash-es'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { Zoom } from 'swiper'
import 'swiper/css/scrollbar'
import 'swiper/css/zoom'
import { Swiper, SwiperSlide } from 'swiper/react'

export default function GallerySlide({
  images = [],
  floorPlanImages = [],
  index = 0,
  alt = ''
}) {

  const { isMobile } = useRecoilValue(global)
  const list = concat(images, floorPlanImages)
  const [swiper, setSwiper] = useState(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const thumbRef = useRef(null)

  const slideTo = useCallback((index) => {
    if (swiper) swiper.slideTo(index, 0)
  }, [swiper])

  // 设置默认siper index
  useEffect(() => {
    slideTo(index)
  }, [slideTo, index])

  // 更新当前index
  const changeHandle = useCallback(sw => {
    setActiveIndex(sw.activeIndex)
  }, [setActiveIndex])

  // 缩略图滚动到当前index, 保持居中
  useEffect(() => {
    if (thumbRef.current) {
      const thumb = thumbRef.current
      const thumbItem = thumb.children[activeIndex]
      if (!thumbItem) return

      const thumbItemWidth = thumbItem.offsetWidth
      const thumbItemOffsetLeft = thumbItem.offsetLeft
      const thumbWidth = thumb.offsetWidth
      const thumbScrollLeft = thumb.scrollLeft
      const thumbScrollLeftCenter = thumbScrollLeft + thumbWidth / 2
      const thumbItemOffsetLeftCenter = thumbItemOffsetLeft + thumbItemWidth / 2
      const thumbItemOffsetLeftCenterDiff = thumbItemOffsetLeftCenter - thumbScrollLeftCenter
      thumb.scrollLeft = thumbItemOffsetLeftCenterDiff + thumbScrollLeft
    }
  }, [activeIndex, thumbRef])

  return (
    <section className='min-w-0 min-h-0 relative'>
      <div className='w-full h-full flex flex-col gap-y-10 md:gap-y-30'>
        <div className='flex-1 min-w-0 min-h-0 max-w-full max-h-full relative xl:px-60'>
          {
            activeIndex > 0 && (
              <PagerButton left onClick={() => swiper.slidePrev()}></PagerButton>
            )
          }
          <Swiper
            modules={[Zoom]}
            zoom={isMobile ? { minRatio: 1, maxRatio: 5 } : false}
            grabCursor
            spaceBetween={4}
            onSlideChange={changeHandle}
            onSwiper={setSwiper}
            className='w-full h-full'
          >
            {
              map(list, (item, i) => (
                <SwiperSlide key={i} className=''>
                  <div className='swiper-zoom-container'>
                    <ResponsiveImageBgLoading
                      src={item}
                      width={2040}
                      height={1210}
                      mobileWidth={1020}
                      mobileHeight={605}
                      alt={`${alt}_${i}`}
                      wrapClassName='w-full h-full'
                      className='w-full h-full block object-contain pointer-events-none'
                    ></ResponsiveImageBgLoading>
                  </div>
                </SwiperSlide>
              ))
            }
          </Swiper>
          {
            activeIndex < list.length - 1 && (
              <PagerButton right onClick={() => swiper.slideNext()}></PagerButton>
            )
          }
        </div>
        <div className='w-full lg:px-60 landscape:hidden xl:landscape:block'>
          <div className='flex overflow-auto' ref={thumbRef}>
            {
              map(list, (item, i) => (
                <div
                  className={classNames(
                    'w-82 h-60 object-cover rounded-sm overflow-hidden cursor-pointer border-2 flex-shrink-0',
                    activeIndex === i ? 'border-secondary' : 'border-transparent'
                  )}
                  key={i}
                  onClick={() => slideTo(i)}
                >
                  <ImageBgLoading
                    src={item}
                    width={60}
                    height={45}
                    alt={`${alt}_${i}`}
                    className='block w-full h-full'
                  ></ImageBgLoading>
                </div>
              ))
            }
          </div>
        </div>
      </div>
    </section>
  )
}

function PagerButton({
  left = false,
  right = false,
  onClick
}) {

  const [pushed, setPushed] = useState(false)
  const clickHandle = useCallback(() => {
    if (!pushed) {
      propertyDataLayer.coverScroll()
    }
    setPushed(true)
    isFunction(onClick) && onClick()
  }, [pushed, setPushed, onClick])

  return (
    <button
      type='button'
      className={classNames(
        'w-32 h-32 rounded-half absolute top-1/2 -translate-y-1/2 shadow-card flex items-center justify-center bg-white z-10 md:w-40 md:h-40',
        { 'left-0': left },
        { 'right-0': right },
      )}
      onClick={clickHandle}
      aria-label={left ? 'slide prev' : 'slide next'}
    >
      {left && <i className='icon icon-arrow-left text-primary md:!text-xl'></i>}
      {right && <i className='icon icon-arrow-right text-primary md:!text-xl'></i>}
    </button>
  )
}